/* Public salon/name index is lazy, shared and independent of product search. */
(() => {
  if (customElements.get('park-salon-search')) return;
  const locale = document.documentElement.lang || undefined;
  const normalize = value => String(value || '').toLocaleLowerCase(locale).replace(/æ/g, 'ae').replace(/ø/g, 'o').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/aa/g, 'a').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const includes = (text, tokens) => tokens.every(token => text.split(' ').some(word => word.startsWith(token)));
  let pending;
  let loaded;
  async function directory(endpoint) {
    if (loaded) return loaded;
    if (pending) return pending;
    pending = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(endpoint, { signal: controller.signal, priority: 'low' });
        if (!response.ok) throw new Error('Salon directory unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = doc.querySelector('template[data-park-salon-index]');
        if (!template) throw new Error('Missing salon directory');
        const seen = new Set();
        loaded = [...template.content.querySelectorAll('[data-id]')].flatMap(node => {
          const data = node.dataset;
          const url = new URL(data.url, location.origin);
          if (!['https:', 'http:'].includes(url.protocol) || seen.has(data.id)) return [];
          seen.add(data.id);
          const staff = JSON.parse(data.staff || '[]').filter(name => typeof name === 'string');
          return [{ ...data, url: url.href, staff, text: normalize([data.name, data.city, data.address].join(' ')), people: staff.map(name => ({ name, text: normalize(name) })) }];
        });
        return loaded;
      } finally { clearTimeout(timeout); pending = null; }
    })();
    return pending;
  }
  function match(salons, term) {
    const query = normalize(term);
    if (query.length < 2) return [];
    const tokens = query.split(' ');
    return salons.flatMap(salon => {
      const names = salon.people.filter(person => includes(`${person.text} ${salon.text}`, tokens) && tokens.some(token => includes(person.text, [token]))).map(person => person.name);
      if (!includes(salon.text, tokens) && !names.length) return [];
      const exact = [salon.name, salon.city, ...names].some(name => normalize(name) === query);
      return [{ salon, names: [...new Set(names)], score: exact ? 0 : names.length ? 1 : 2 }];
    }).sort((a, b) => a.score - b.score || a.salon.city.localeCompare(b.salon.city, locale) || a.salon.name.localeCompare(b.salon.name, locale));
  }
  class ParkSalonSearch extends HTMLElement {
    static get observedAttributes() { return ['data-query']; }
    connectedCallback() { this.update(); }
    attributeChangedCallback() { if (this.isConnected) this.update(); }
    disconnectedCallback() { this.sequence = (this.sequence || 0) + 1; }
    link(url) {
      const result = new URL(url, location.origin);
      if (result.origin === location.origin && this.dataset.previewMode !== 'main') result.searchParams.set('preview_theme_id', this.dataset.preview);
      return result.href;
    }
    async update() {
      const sequence = this.sequence = (this.sequence || 0) + 1;
      const term = (this.dataset.query || '').trim().slice(0, 100);
      this.replaceChildren();
      this.removeAttribute('data-has-matches');
      this.hidden = normalize(term).length < 2;
      if (this.hidden) return;
      if (!this.hidden) {
        const status = document.createElement('p'); status.className = 'cc-park-salon-search__status'; status.setAttribute('role', 'status'); status.textContent = this.dataset.loading; this.append(status);
      }
      try {
        const salons = await directory(this.dataset.endpoint);
        if (!this.isConnected || sequence !== this.sequence || normalize(term).length < 2) return;
        this.render(match(salons, term), term);
      } catch {
        if (!this.isConnected || sequence !== this.sequence || normalize(term).length < 2) return;
        this.replaceChildren();
        const p = document.createElement('p'); p.setAttribute('role', 'status'); p.textContent = this.dataset.error; this.append(p);
      }
    }
    render(matches, term) {
      this.replaceChildren();
      this.toggleAttribute('data-has-matches', matches.length > 0);
      this.hidden = !matches.length && !this.hasAttribute('data-full-page');
      if (this.hidden) return;
      const heading = document.createElement('h2'); heading.className = 'park-search-heading'; heading.textContent = this.dataset.title; this.append(heading);
      const status = document.createElement('p'); status.className = matches.length ? 'visually-hidden' : ''; status.setAttribute('role', 'status'); status.textContent = matches.length ? this.dataset.count.replace('[count]', matches.length) : this.dataset.empty; this.append(status);
      const list = document.createElement('ul'); list.className = 'cc-park-salon-search__list';
      const visible = this.hasAttribute('data-full-page') ? matches : matches.slice(0, 5);
      visible.forEach(({ salon, names }) => {
        const li = document.createElement('li'); const a = document.createElement('a'); a.className = 'cc-park-salon-search__result'; a.href = this.link(salon.url);
        if (salon.image) { const img = document.createElement('img'); img.src = salon.image; img.alt = ''; img.width = 48; img.height = 48; img.loading = 'lazy'; a.append(img); }
        const copy = document.createElement('span');
        const title = document.createElement('strong'); title.textContent = names.length ? names.join(', ') : ['PARK Styling', salon.city, salon.name].filter(Boolean).join(' · '); copy.append(title);
        const subtitle = document.createElement('span'); subtitle.textContent = names.length ? [salon.city, salon.name].filter(Boolean).join(' · ') : salon.address; copy.append(subtitle); a.append(copy); li.append(a); list.append(li);
      });
      this.append(list);
      if (!this.hasAttribute('data-full-page') && matches.length > 5) {
        const all = document.createElement('a'); const url = new URL(this.dataset.searchUrl, location.origin); url.searchParams.set('q', term); url.searchParams.set('type', 'product'); url.hash = 'park-salon-results'; all.href = this.link(url.href); all.className = 'cc-park-salon-search__all'; all.textContent = this.dataset.all; this.append(all);
      }
    }
  }
  // A committed full-page search must navigate even when the base theme's
  // search-as-you-type submit handler only blurs the field.
  document.addEventListener('submit', event => {
    if (event.target.matches('.main-search__form') && event.target.closest('[data-section-type="search-template"]')) event.stopImmediatePropagation();
  }, true);
  customElements.define('park-salon-search', ParkSalonSearch);
})();
